import { useState, type FormEvent } from 'react';
import { errorMessage, useLocale } from '../../i18n';
import { Field, StatusMessage } from '../ui';
import {
  fetchPlatformReport as defaultFetch,
  type PlatformReport,
  type ReportGrouping,
  type ReportPeriod,
  type ReportResult,
} from '../../lib/api/platformReports';
import { buildReportTable, reportTableToCsv } from './reportsTable';

// ─────────────────────────────────────────────────────────────────────
// /sa → Rapporter (#1043). The platform's figures, READ-ONLY.
//
// «How many clubs and centres are active, how many bookings this month, how
// many players sign in, what did card payments and platform fees come to?»
// needed a developer and a database. This is where StarPoint staff read it.
//
// WHAT IT DOES NOT DO: compute anything. The server validates the period,
// decides who may read (`platform.reports.read`, Entra only), aggregates in
// SQL and records each read (`reports.read`). This picks a period, asks, and
// renders the table — and exports exactly that table as CSV.
//
// NO REPORT UNTIL ASKED. Every read is recorded, so opening the section must
// not be one: the reader presses «Vis rapport», and the lead says it is
// logged.
//
// AGGREGATES ONLY. There is no person on this screen; the one name is an
// organization's, on the per-organization view.
// ─────────────────────────────────────────────────────────────────────

export interface ReportsSectionProps {
  /** Injectable for tests; defaults to the real client. */
  load?: (period: ReportPeriod) => Promise<ReportResult>;
  /** Injectable for tests; defaults to a browser download. */
  download?: (filename: string, content: string) => void;
  /** Injectable for tests: «now», for the default month and the year list. */
  now?: () => Date;
}

type Mode = 'month' | 'quarter' | 'custom';

const pad2 = (n: number) => String(n).padStart(2, '0');
/** Days in a month, from the local calendar. */
const daysIn = (year: number, month: number) => new Date(year, month, 0).getDate();
/** A local month key — never `toISOString()` (DOMAIN_RULES §4). */
const monthKey = (d: Date) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`;

function monthPeriod(ym: string): { from: string; to: string } | null {
  const m = /^(\d{4})-(\d{2})$/.exec(ym);
  if (!m) return null;
  const [y, mo] = [Number(m[1]), Number(m[2])];
  if (mo < 1 || mo > 12) return null;
  return { from: `${ym}-01`, to: `${ym}-${pad2(daysIn(y, mo))}` };
}

function quarterPeriod(year: number, quarter: number): { from: string; to: string } {
  const first = (quarter - 1) * 3 + 1;
  const last = first + 2;
  return {
    from: `${year}-${pad2(first)}-01`,
    to: `${year}-${pad2(last)}-${pad2(daysIn(year, last))}`,
  };
}

function defaultDownload(filename: string, content: string): void {
  const url = URL.createObjectURL(new Blob([content], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

type Outcome =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ok'; report: PlatformReport }
  | { kind: 'failed'; text: string };

export function ReportsSection({
  load = defaultFetch,
  download = defaultDownload,
  now = () => new Date(),
}: ReportsSectionProps) {
  const { t, formatDate, formatNumber } = useLocale();
  const [today] = useState(now);
  const [mode, setMode] = useState<Mode>('month');
  const [month, setMonth] = useState(() => monthKey(today));
  const [year, setYear] = useState(() => today.getFullYear());
  const [quarter, setQuarter] = useState(() => Math.floor(today.getMonth() / 3) + 1);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [groupBy, setGroupBy] = useState<ReportGrouping>('month');
  const [outcome, setOutcome] = useState<Outcome>({ kind: 'idle' });

  const years = Array.from({ length: 4 }, (_, i) => today.getFullYear() - i);

  const refusal = (result: Exclude<ReportResult, { state: 'ok' }>): string => {
    if (result.state === 'no-api') return t('saReports.error.noApi');
    if (result.state === 'error') return t('saReports.error.failed');
    if (result.code === 'period_too_long') return t('saReports.error.tooLong');
    if (result.code === 'invalid_filter' || result.code === 'unknown_filter') {
      return t('saReports.error.invalidPeriod');
    }
    if (result.code === 'forbidden') return t('saReports.error.forbidden');
    if (result.code === 'entra_required') return t('saReports.error.entraRequired');
    return errorMessage(t, result.code);
  };

  const period = (): { from: string; to: string } | null => {
    if (mode === 'month') return monthPeriod(month);
    if (mode === 'quarter') return quarterPeriod(year, quarter);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(from) || !/^\d{4}-\d{2}-\d{2}$/.test(to) || to < from) {
      return null;
    }
    return { from, to };
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const p = period();
    if (!p) {
      setOutcome({ kind: 'failed', text: t('saReports.error.invalidPeriod') });
      return;
    }
    setOutcome({ kind: 'loading' });
    const result = await load({ ...p, groupBy });
    setOutcome(
      result.state === 'ok'
        ? { kind: 'ok', report: result.report }
        : { kind: 'failed', text: refusal(result) },
    );
  };

  const report = outcome.kind === 'ok' ? outcome.report : null;
  const table = report ? buildReportTable(report, { t, formatDate, formatNumber }) : null;

  const exportCsv = () => {
    if (!report || !table) return;
    const { from: f, to: tt, groupBy: g } = report.period;
    download(`starpoint-${f}-${tt}-${g}.csv`, reportTableToCsv(table));
  };

  const radio = (
    name: string,
    value: string,
    checked: boolean,
    onChange: () => void,
    label: string,
  ) => (
    <label className="inline-flex items-center gap-1.5 text-xs text-ink mr-4">
      <input
        type="radio"
        name={name}
        value={value}
        checked={checked}
        onChange={onChange}
        data-testid={`sa-reports-${name}-${value}`}
      />
      {label}
    </label>
  );

  return (
    <section className="card p-6" data-testid="sa-reports">
      <h1 className="font-display text-2xl font-semibold text-ink">{t('saReports.title')}</h1>
      <p className="text-sm text-ink-3 mt-1">{t('saReports.lead')}</p>

      <form
        className="mt-5"
        onSubmit={(e) => void submit(e)}
        data-testid="sa-reports-form"
        noValidate
      >
        <fieldset className="mb-3">
          <legend className="block font-mono text-[11px] font-semibold uppercase tracking-wider text-ink-3 mb-1.5">
            {t('saReports.period')}
          </legend>
          {radio(
            'mode',
            'month',
            mode === 'month',
            () => setMode('month'),
            t('saReports.mode.month'),
          )}
          {radio(
            'mode',
            'quarter',
            mode === 'quarter',
            () => setMode('quarter'),
            t('saReports.mode.quarter'),
          )}
          {radio(
            'mode',
            'custom',
            mode === 'custom',
            () => setMode('custom'),
            t('saReports.mode.custom'),
          )}
        </fieldset>

        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
          {mode === 'month' && (
            <Field label={t('saReports.month')}>
              <input
                type="month"
                className="input w-full text-xs"
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                data-testid="sa-reports-month"
              />
            </Field>
          )}
          {mode === 'quarter' && (
            <>
              <Field label={t('saReports.year')}>
                <select
                  className="input w-full text-xs"
                  value={year}
                  onChange={(e) => setYear(Number(e.target.value))}
                  data-testid="sa-reports-year"
                >
                  {years.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label={t('saReports.quarter')}>
                <select
                  className="input w-full text-xs"
                  value={quarter}
                  onChange={(e) => setQuarter(Number(e.target.value))}
                  data-testid="sa-reports-quarter"
                >
                  {[1, 2, 3, 4].map((q) => (
                    <option key={q} value={q}>
                      {t('saReports.quarterN', { n: String(q) })}
                    </option>
                  ))}
                </select>
              </Field>
            </>
          )}
          {mode === 'custom' && (
            <>
              <Field label={t('saReports.from')}>
                <input
                  type="date"
                  className="input w-full text-xs"
                  value={from}
                  onChange={(e) => setFrom(e.target.value)}
                  data-testid="sa-reports-from"
                />
              </Field>
              <Field label={t('saReports.to')} hint={t('saReports.hint.to')}>
                <input
                  type="date"
                  className="input w-full text-xs"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                  data-testid="sa-reports-to"
                />
              </Field>
            </>
          )}
        </div>

        <fieldset className="mt-3">
          <legend className="block font-mono text-[11px] font-semibold uppercase tracking-wider text-ink-3 mb-1.5">
            {t('saReports.groupBy')}
          </legend>
          {radio(
            'groupBy',
            'month',
            groupBy === 'month',
            () => setGroupBy('month'),
            t('saReports.groupBy.month'),
          )}
          {radio(
            'groupBy',
            'organization',
            groupBy === 'organization',
            () => setGroupBy('organization'),
            t('saReports.groupBy.organization'),
          )}
        </fieldset>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <button
            type="submit"
            className="btn-primary text-xs"
            disabled={outcome.kind === 'loading'}
            data-testid="sa-reports-show"
          >
            {outcome.kind === 'loading' ? t('saReports.loading') : t('saReports.show')}
          </button>
          <span className="text-[11px] text-ink-3">{t('saReports.logged')}</span>
        </div>
      </form>

      <div className="mt-6" aria-live="polite">
        {outcome.kind === 'idle' && (
          <p className="text-[11px] text-ink-3" data-testid="sa-reports-idle">
            {t('saReports.idle')}
          </p>
        )}
        {outcome.kind === 'failed' && (
          <div data-testid="sa-reports-failed">
            <StatusMessage kind="error">{outcome.text}</StatusMessage>
          </div>
        )}
      </div>

      {report && table && (
        <div className="mt-2" data-testid="sa-reports-result">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-[11px] text-ink-3" data-testid="sa-reports-period">
              {t('saReports.showing', {
                from: formatDate(new Date(`${report.period.from}T00:00:00`), { dateStyle: 'long' }),
                to: formatDate(new Date(`${report.period.to}T00:00:00`), { dateStyle: 'long' }),
              })}
            </p>
            <button
              type="button"
              className="btn-ghost text-xs"
              onClick={exportCsv}
              data-testid="sa-reports-csv"
            >
              {t('saReports.csv')}
            </button>
          </div>
          <div className="overflow-x-auto mt-2">
            <table className="w-full text-xs" data-testid="sa-reports-table">
              <caption className="sr-only">
                {report.months ? t('saReports.caption.month') : t('saReports.caption.organization')}
              </caption>
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-ink-3">
                  {table.columns.map((c) => (
                    <th
                      key={c.id}
                      scope="col"
                      className={`py-1 pr-3 font-medium align-bottom ${c.numeric ? 'text-right' : ''}`}
                    >
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {table.rows.map((row) => (
                  <tr
                    key={row.key}
                    className="border-t border-white/10"
                    data-testid={`sa-reports-row-${row.key}`}
                  >
                    {row.cells.map((cell, i) =>
                      i === 0 ? (
                        <th
                          key={table.columns[i]!.id}
                          scope="row"
                          className="py-1.5 pr-3 text-left font-normal whitespace-nowrap"
                        >
                          {cell.display}
                        </th>
                      ) : (
                        <td
                          key={table.columns[i]!.id}
                          className={`py-1.5 pr-3 whitespace-nowrap ${table.columns[i]!.numeric ? 'text-right tabular-nums' : ''}`}
                        >
                          {cell.display}
                        </td>
                      ),
                    )}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr
                  className="border-t-2 border-white/10 font-semibold"
                  data-testid="sa-reports-total"
                >
                  {table.total.map((cell, i) =>
                    i === 0 ? (
                      <th
                        key={table.columns[i]!.id}
                        scope="row"
                        className="py-1.5 pr-3 text-left whitespace-nowrap"
                      >
                        {cell.display}
                      </th>
                    ) : (
                      <td
                        key={table.columns[i]!.id}
                        className={`py-1.5 pr-3 whitespace-nowrap ${table.columns[i]!.numeric ? 'text-right tabular-nums' : ''}`}
                      >
                        {cell.display}
                      </td>
                    ),
                  )}
                </tr>
              </tfoot>
            </table>
          </div>

          <ul
            className="mt-4 space-y-1 text-[11px] text-ink-3 list-disc pl-4"
            data-testid="sa-reports-notes"
          >
            {report.organizations && (
              <li data-testid="sa-reports-accounts">
                {t('saReports.note.accountsPlatformWide', {
                  signedIn: formatNumber(report.total.signedInAccounts),
                  created: formatNumber(report.total.newAccounts),
                })}
              </li>
            )}
            {report.months && <li>{t('saReports.note.distinct')}</li>}
            <li>{t('saReports.note.money')}</li>
            <li>{t('saReports.note.fees')}</li>
            {report.otherCurrencyEntries > 0 && (
              <li data-testid="sa-reports-other-currency">
                {t('saReports.note.otherCurrency', { count: report.otherCurrencyEntries })}
              </li>
            )}
          </ul>
        </div>
      )}
    </section>
  );
}
